import React, { memo, useState } from 'react';
import { Td, Text, Tr } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useSelector } from 'react-redux';

import { getPlanPageThemeColors, PlanPageThemeColors } from 'redux/ThemeSlice';
import { getDataTypeBasedValuewithNewValue } from 'utils/FormatValues';

import {
  PLAN_KPI_SELECTED_BG,
  PLAN_KPI_SELECTED_BORDER,
  PLAN_TABLE_CELL_BORDER,
  PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
  PLAN_TEXT,
} from '../planPageRevampUi';

interface Properties {
  row: any;
  expandKey: string;
}

const formatCell = (row: any, item: any) => {
  if (item?.value === null) return row?.format_properties?.null_value ?? '-';
  return getDataTypeBasedValuewithNewValue(
    row?.data_format,
    item?.value || 0,
    row?.format_properties,
  );
};

const getCellBackground = (
  themeColors: PlanPageThemeColors,
  item: any,
  expandKey: string,
  isSubRow?: boolean,
) => {
  if (item?.isExpandedColumn) {
    return PLAN_KPI_SELECTED_BG;
  }
  if (item?.name === expandKey) {
    return isSubRow ? themeColors.border : PLAN_KPI_SELECTED_BG;
  }
  if (item?.isActualValue) {
    return PLAN_KPI_SELECTED_BG;
  }
  return themeColors.surface;
};

const PlanDataTableRow = ({ row, expandKey }: Properties) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const themeColors = useSelector(getPlanPageThemeColors);
  const hasSubRows = Boolean(row?.dim_item_data && row.dim_item_data.length > 0);

  const subRows = hasSubRows
    ? row.dim_item_data.map((item: any) => ({
        name: item?.name ?? '',
        values: item?.value || [],
      }))
    : [];

  const metricCellStyles = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    bg: PLAN_KPI_SELECTED_BG,
    py: 3,
    px: 3,
    minW: PLAN_TABLE_METRIC_COLUMN_MIN_WIDTH,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_KPI_SELECTED_BORDER,
    ...PLAN_TEXT.tableComfortaa,
    color: themeColors.textDark,
    whiteSpace: 'nowrap' as const,
  };

  const valueCellStyles = {
    py: 3,
    px: 5,
    ...PLAN_TEXT.tableComfortaaCell,
    color: themeColors.textDark,
    textAlign: 'end' as const,
    borderBottom: '1px solid',
    borderRight: '1px solid',
    borderColor: PLAN_TABLE_CELL_BORDER,
    whiteSpace: 'nowrap' as const,
  };

  return (
    <>
      <Tr
        onClick={() => hasSubRows && setIsExpanded((previous) => !previous)}
        cursor={hasSubRows ? 'pointer' : 'default'}
        bg={themeColors.surface}
        _hover={{ bg: themeColors.surface }}
      >
        <Td {...metricCellStyles}>
          <Text as='span' display='inline-flex' alignItems='center' gap='10px'>
            {hasSubRows &&
              (isExpanded ? (
                <ChevronDownIcon boxSize={3.5} color={themeColors.textDark} />
              ) : (
                <ChevronRightIcon boxSize={3.5} color={themeColors.textDark} />
              ))}
            {row?.name}
          </Text>
        </Td>
        {(row?.total_values || []).map((item: any, index: number) => {
          if (!item?.isShow) return <React.Fragment key={`${item?.name ?? ''}-${index}`} />;
          return (
            <Td
              key={`${item?.name ?? ''}-${index}`}
              {...valueCellStyles}
              bg={getCellBackground(themeColors, item, expandKey)}
            >
              {formatCell(row, item)}
            </Td>
          );
        })}
      </Tr>

      {isExpanded &&
        subRows.map(
          (subRow: any, subRowIndex: number) =>
            subRow.values.length > 0 && (
              <Tr key={subRow.name + subRowIndex} bg={themeColors.surface}>
                <Td
                  {...metricCellStyles}
                  pl={8}
                  {...PLAN_TEXT.tableComfortaaCell}
                  color={themeColors.textSecondary}
                >
                  {subRow.name}
                </Td>
                {subRow.values.map((item: any, index: number) => {
                  if (!item?.isShow) return <React.Fragment key={`${item?.name ?? ''}-${index}`} />;
                  return (
                    <Td
                      key={`${item?.name ?? ''}-${index}`}
                      {...valueCellStyles}
                      color={themeColors.textSecondary}
                      bg={getCellBackground(themeColors, item, expandKey, true)}
                    >
                      {formatCell(row, item)}
                    </Td>
                  );
                })}
              </Tr>
            ),
        )}
    </>
  );
};

export default memo(PlanDataTableRow);
